import {
  BulkLockPaginationUpperLimitConfig,
  CollapseNonWorkingTimeConfig,
  Company,
  Group,
  MultiSelectOption,
  Office,
  Role,
  TaskGroup,
  TaskPeril,
  Team,
  UIValidationRulesResponse,
} from '@dispatch/shared/types';
import * as React from 'react';
import type { QueryStatus } from 'react-query';
import { useGetCompany } from './UseGetCompany';
import { useGetPerils } from '../task-templates';
import { useAccessToken, useCurrentUser } from '@dispatch/web/auth';
import { useIsAuthenticated } from '@azure/msal-react';
import { useGetRescheduleReasons } from '../reschedule-reasons/UseGetRescheduleReasons';
import { useGetCancelReasons } from '../cancel-reasons/UseGetCancelReasons';
import { useGetRoles } from '../roles';
import { useGetTaskGroups } from '../task-group';
import { useGetEfficiencies } from '../resource';
import { useGetRegions } from '../region/UseGetRegions';
import {
  useGetBulkLockPaginationUpperLimit,
  useGetCollapseNonWorkingTimeConfig,
  useGetNaqSelfServiceFeature,
  useGetQualifiedResourcesFNOLFeature,
  useTrimLeadingZerosFeature,
} from '../task';
import {
  useGetClaimOwnerCapacityBandFeature,
  useGetFieldCapacityByDayFeature,
  useGetFlagExcludeCertsAndLicenses,
} from '../resource';
import { useGetUIValidationRules } from '../task/UseGetUIValidationRules';

export const CompanyContext = React.createContext<
  | {
      company: Company;
      // Lets consumers tell a still-loading company read from an empty one.
      companyStatus: QueryStatus;
      offices?: Office[];
      teams?: Team[];
      roles?: Role[];
      groups?: Group[];
      perilOptions?: MultiSelectOption[];
      perils?: TaskPeril[];
      rescheduleReasons?: MultiSelectOption[];
      cancelReasons?: MultiSelectOption[];
      cancelReasonsStatus?: any;
      taskGroups?: TaskGroup[];
      efficiencyOptions?: MultiSelectOption[];
      regionOptions?: MultiSelectOption[];
      featureFlags: {
        qualifiedResourcesFnol: boolean;
        collapseNonWorkingTimeConfig?: CollapseNonWorkingTimeConfig;
        trimLeadingZeros?: boolean;
        excludeCertsAndLicenses?: boolean;
        naqSelfServiceEnabled: boolean;
        bulkLockPaginationUpperLimit?: BulkLockPaginationUpperLimitConfig;
        // Kill-switch for the per-day field capacity model (ADO #94651).
        // Always a boolean, defaulted to `false`, so an unresolved query renders
        // the legacy single Field Capacity input.
        fieldCapacityByDayEnabled: boolean;
        // Kill-switch for the Claim Owner capacity band on the Scheduler View
        // resource row (ADO #104914). Always a boolean, defaulted to `false`,
        // so an unresolved or absent flag leaves the Scheduler unchanged.
        claimOwnerCapacityBand: boolean;
      };
      uiValidationRules: UIValidationRulesResponse;
    }
  | undefined
>(undefined);

function CompanyProvider({ children }: { children: React.ReactChild }) {
  const isAuthenticated = useIsAuthenticated();
  const { isAccessTokenSet } = useAccessToken();
  const { tenantId } = useCurrentUser();
  const { data: taskGroups } = useGetTaskGroups(
    isAuthenticated && isAccessTokenSet
  );
  const qualifiedResourcesFnol = useGetQualifiedResourcesFNOLFeature(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );
  const uiValidationRules = useGetUIValidationRules(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const collapseNonWorkingTimeConfig = useGetCollapseNonWorkingTimeConfig(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );
  const trimLeadingZerosFeature = useTrimLeadingZerosFeature(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const excludeCertsAndLicenses = useGetFlagExcludeCertsAndLicenses(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const naqSelfServiceFeature = useGetNaqSelfServiceFeature(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const bulkLockPaginationUpperLimit = useGetBulkLockPaginationUpperLimit(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const fieldCapacityByDayFeature = useGetFieldCapacityByDayFeature(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const claimOwnerCapacityBandFeature = useGetClaimOwnerCapacityBandFeature(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const featureFlags = {
    qualifiedResourcesFnol: qualifiedResourcesFnol.feature,
    collapseNonWorkingTimeConfig: collapseNonWorkingTimeConfig.feature,
    trimLeadingZeros: trimLeadingZerosFeature.feature,
    excludeCertsAndLicenses: excludeCertsAndLicenses.excludeCertsAndLicenses,
    naqSelfServiceEnabled: naqSelfServiceFeature.feature,
    bulkLockPaginationUpperLimit: bulkLockPaginationUpperLimit.feature,
    fieldCapacityByDayEnabled: fieldCapacityByDayFeature.feature,
    claimOwnerCapacityBand: claimOwnerCapacityBandFeature.feature,
  };

  const { rescheduleReasons } = useGetRescheduleReasons(
    {
      tenantId: tenantId || '',
    },
    isAuthenticated && isAccessTokenSet
  );

  const { cancelReasons, cancelReasonStatus } = useGetCancelReasons(
    {
      tenantId: tenantId || '',
    },
    isAuthenticated && isAccessTokenSet
  );

  const company = useGetCompany(isAuthenticated && isAccessTokenSet);

  const { perils } = useGetPerils(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const { data: roles } = useGetRoles(
    undefined,
    isAuthenticated && isAccessTokenSet
  );

  const perilOptions: MultiSelectOption[] = React.useMemo(() => {
    return perils?.map((peril) => ({
      title: peril.description,
      value: peril.code,
    }));
  }, [perils]);

  const { data: efficiencies } = useGetEfficiencies(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const efficiencyOptions: MultiSelectOption[] = React.useMemo(() => {
    return efficiencies?.map((efficiency) => ({
      title: `${efficiency}%`,
      value: efficiency,
    }));
  }, [efficiencies]);

  const { data: regions } = useGetRegions({
    tenantId: tenantId || '',
    enabled: isAuthenticated && isAccessTokenSet,
  });

  const regionOptions: MultiSelectOption[] = React.useMemo(() => {
    return (
      regions?.data?.map((region) => ({
        title: region.name,
        value: region.name,
      })) || []
    );
  }, [regions]);

  const value = {
    company: { ...(company.companyData as Company) },
    companyStatus: company.companyStatus,
    offices: company?.companyData?.office,
    teams: company?.companyData?.team,
    groups: company?.companyData?.group,
    roles,
    rescheduleReasons,
    cancelReasons: cancelReasons,
    cancelReasonsStatus: cancelReasonStatus,
    perilOptions,
    perils,
    taskGroups,
    efficiencyOptions,
    regionOptions,
    featureFlags,
    uiValidationRules: uiValidationRules.feature || { padClaimNumber: false },
  };

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
}

function useCompany() {
  const context = React.useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}

export { CompanyProvider, useCompany };
